import type { Texture } from "pixi.js";
import type { Log, PresentationTaskRunner } from "@slot/presentation";
import { EMPTY_POTS, POTS, POT_CAPACITY, type PotKind, type PotLevels } from "../../rules/symbols";
import type { EffectsLayer } from "../../scene/EffectsLayer";
import { POT_COLOURS, type PotsView } from "../../scene/PotsView";
import { POT_ERUPTION_TUNING, POTS_TUNING } from "../../tuning/featureTuning";
import type { Boosters, UpdatePots } from "../wire";
import { FX_SCOPE } from "../../scene/fxScope";

/** Where a line's bet is read from (the shell's `BetLinesPort`, structurally; decision 56). */
export interface BetSource {
  getSnapshot(): { lineBetMinor: string };
  subscribe(listener: () => void): () => void;
}

/**
 * The pots: each base round's gems flying from their coins into the pots (`update_pots`), the charged pots erupting into
 * the bonus's boosters (`activate_boosters`), LIFE's refill and MULTI's eruptions in the bonus, and the bet's rule: the
 * pots hold at the line's bet the last round was played at, so while the player picks another bet they show empty,
 * and back at that bet they show their gems again; another line count keeps them — the server's rule, stated by every
 * round's `levelsBefore` at its `betMinor` (a line's bet).
 */
export class PotsPresenter {
  private readonly view: PotsView;
  private readonly fx: EffectsLayer;
  private readonly gems: Readonly<Record<PotKind, Texture | null>>;
  private readonly cellCentre: (index: number) => { x: number; y: number };
  private readonly onBoosters: (boosters: Readonly<Record<PotKind, boolean>>) => void;
  private readonly runner: PresentationTaskRunner;
  private readonly log: Log;
  private readonly shine: { potCharged(kind: PotKind): void } | null;
  private readonly coinBursts: { burst(kind: PotKind): void } | null;
  private readonly unsubscribe: () => void;
  /** The levels the last base round left, and the line's bet it was played at (null before the first round). */
  private lastRound: { betMinor: string; levels: PotLevels } | null = null;
  private shown: PotLevels = EMPTY_POTS;
  private inBonus = false;

  public constructor(input: {
    view: PotsView;
    fx: EffectsLayer;
    gems: Readonly<Record<PotKind, Texture | null>>;
    /** A cell's centre in the composition's space. */
    cellCentre: (index: number) => { x: number; y: number };
    bet: BetSource;
    /** The boosters the next bonus would start with (the trigger panel lists them). */
    onBoosters: (boosters: Readonly<Record<PotKind, boolean>>) => void;
    runner: PresentationTaskRunner;
    log: Log;
    /** The pot's glint as a gem lands in it or it erupts. */
    shine?: { potCharged(kind: PotKind): void };
    /** The coins a pot spills as a gem's light reaches it. */
    coinBursts?: { burst(kind: PotKind): void };
  }) {
    this.view = input.view;
    this.fx = input.fx;
    this.gems = input.gems;
    this.cellCentre = input.cellCentre;
    this.onBoosters = input.onBoosters;
    this.runner = input.runner;
    this.log = input.log;
    this.shine = input.shine ?? null;
    this.coinBursts = input.coinBursts ?? null;
    this.view.setLevels(EMPTY_POTS);
    this.unsubscribe = input.bet.subscribe(() => this.onBetChanged(input.bet.getSnapshot().lineBetMinor));
  }

  /** The pots the player sees now (the dev hook's read). */
  public get levels(): PotLevels {
    return this.shown;
  }

  /** A base round's pots: the levels it starts from, each gem flying from its coin into its pot, the levels it leaves. */
  public async presentSpinPots(input: UpdatePots): Promise<void> {
    this.show(input.levelsBefore);
    const levels: Record<PotKind, number> = { ...input.levelsBefore };
    const flights = input.marks.map(async (mark, i) => {
      await this.runner.sleep(i * POTS_TUNING.gemStaggerSeconds, FX_SCOPE);
      const from = this.cellCentre(mark.index);
      const to = this.view.mouth(mark.pot);
      const tint = POT_COLOURS[mark.pot].glow;
      this.fx.burst({ at: from, tint, count: 6, radius: 90, seconds: 0.4 });
      // The gem flies as its pot's light (the gem's own sprite until the lights are in) into the opening; its impact
      // there leads it in, the potion's level switching under the flash, and the pot spills a few coins.
      await this.fx.flyLight({
        pot: mark.pot,
        from,
        to,
        lift: POTS_TUNING.gemLift,
        seconds: POTS_TUNING.gemFlightSeconds,
        fallback: { texture: this.gems[mark.pot], size: POTS_TUNING.gemSize, tint, scale: { from: 0.6, peak: 1.25, to: 0.7 } },
        beforeArrival: {
          seconds: POTS_TUNING.impactLeadSeconds,
          run: () => {
            this.fx.impact(mark.pot, to, tint);
            this.coinBursts?.burst(mark.pot);
            // A full pot still takes the gem; its level stays full.
            levels[mark.pot] = Math.min(POT_CAPACITY, levels[mark.pot] + 1);
            this.view.setLevel(mark.pot, levels[mark.pot]);
          },
        },
      });
      this.shine?.potCharged(mark.pot);
      await this.view.bump(mark.pot, POTS_TUNING.bumpSeconds);
    });
    await Promise.all(flights);
    this.show(input.levelsAfter);
    this.lastRound = { betMinor: input.betMinor, levels: input.levelsAfter };
    this.onBoosters({ life: input.levelsAfter.life > 0, multi: input.levelsAfter.multi > 0, grow: input.levelsAfter.grow > 0 });
    if (input.marks.length > 0) this.log(`[Pots] ${input.marks.map((m) => `${m.pot}@${m.index}`).join(" ")} → ${POTS.map((p) => input.levelsAfter[p]).join("/")}`);
  }

  /**
   * The bonus starts: each booster's pot lights in turn (the Rive pots overflow, its plume rising). One with work on the
   * board at the start (`landings`: LIFE's flames round the meter, GROW's grid) erupts, lighting at the eruption's
   * height, and its landing runs as the eruption's jets leave the screen (its fire streak, and what it lights there)
   * before the next, a beat more once it has landed (`afterLandingSeconds`); any other only lights (MULTI erupts as it
   * throws its badges). The pots empty, the boosters' glow for the bonus.
   */
  public async activateBoosters(boosters: Boosters, landings: Partial<Record<PotKind, () => Promise<void>>>): Promise<void> {
    this.inBonus = true;
    for (const [index, kind] of POTS.entries()) {
      if (!boosters[kind]) continue;
      this.shine?.potCharged(kind);
      const landing = landings[kind];
      if (landing) {
        const eruption = this.eruptPot(kind, () => this.view.setBooster(kind, "lit"));
        await this.untilStreaks(eruption);
        await landing();
        await eruption;
        // Its landing reads on its own before the next booster's turn.
        if (POTS.slice(index + 1).some((next) => boosters[next])) await this.runner.sleep(POTS_TUNING.afterLandingSeconds, FX_SCOPE);
      } else this.view.setBooster(kind, "lit");
      await this.runner.sleep(POTS_TUNING.eruptStaggerSeconds, FX_SCOPE);
    }
    this.show(EMPTY_POTS);
    for (const kind of POTS) this.view.setBooster(kind, boosters[kind] ? "lit" : "off");
    // The bonus spends every pot: the next base round starts from empty ones at this bet.
    if (this.lastRound) this.lastRound = { betMinor: this.lastRound.betMinor, levels: EMPTY_POTS };
  }

  /**
   * LIFE refills the meter: its pot erupts, its booster spent at the eruption's height (the Rive pot back to its gems');
   * its fire streak sets out as the eruption's jets leave the screen.
   */
  public async refill(): Promise<void> {
    await this.untilStreaks(this.eruptPot("life", () => this.view.setBooster("life", "spent")));
  }

  /** MULTI erupts: its fire streaks set out as the eruption's jets leave the screen (the pot's swell plays on). */
  public erupt(kind: PotKind): Promise<void> {
    return this.untilStreaks(this.eruptPot(kind));
  }

  /** Where `kind`'s mouth is, in the composition's space (MULTI's badges fly from it). */
  public mouth(kind: PotKind): { x: number; y: number } {
    return this.view.mouth(kind);
  }

  /** The bonus is over: no booster glows, the pots are empty. */
  public endBonus(): void {
    this.inBonus = false;
    for (const kind of POTS) this.view.setBooster(kind, "off");
    this.show(EMPTY_POTS);
  }

  /** A recovery: the bonus's glow goes, the pots show the last round's levels at the current line's bet. */
  public reset(lineBetMinor: string): void {
    this.endBonus();
    this.onBetChanged(lineBetMinor);
  }

  public dispose(): void {
    this.unsubscribe();
  }

  /** A pot erupts: the artist's eruption on its opening, the pot shaking and swelling under it; `atPeak` at its height. */
  private eruptPot(kind: PotKind, atPeak?: () => void): Promise<void> {
    this.fx.erupt(kind);
    return this.view.erupt(kind, POTS_TUNING.eruptSeconds, atPeak);
  }

  /** The wait for an eruption's fire streaks (the artist's: as its jets leave the screen); the eruption plays on. */
  private async untilStreaks(eruption: Promise<void>): Promise<void> {
    await Promise.race([eruption, this.runner.sleep(POT_ERUPTION_TUNING.streaksAfterSeconds, FX_SCOPE)]);
  }

  private onBetChanged(lineBetMinor: string): void {
    if (this.inBonus) return;
    const last = this.lastRound;
    this.show(last && last.betMinor === lineBetMinor ? last.levels : EMPTY_POTS);
  }

  private show(levels: PotLevels): void {
    this.shown = levels;
    this.view.setLevels(levels);
  }
}
